// Component: PositionsTable.jsx
// Purpose: Reusable positions table — separate unrealized/realized P&L columns, broker snapshot, reconcile-state (R105.1, R105.2, R104.5)

import React, { useEffect, useState } from 'react';
import { useLivePnL } from '../../contexts/TradingDataContext';
import { derivePositionRowKey } from '../../utils/positionsRegistry';
import { getSourceTagBadge, resolveProductLabel } from '../../utils/tradingTransforms';
import { Badge, Button, DataTable } from '../ds';
import ReconcileBadge from './ReconcileBadge';
import RecoveryActionModal from './RecoveryActionModal';
import { services } from '../../services/api';
import { useSnackbar } from '../../contexts/SnackbarContext';

// --> Price formatter (en-IN locale, 2 decimal places)
const formatPrice = (val) => {
  if (val == null) return '—';
  const num = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(num)) return '—';
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// --> P&L formatter — prefixes '+' for positive values, colours handled by caller
const formatPnl = (val) => {
  if (val == null) return '—';
  const num = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(num)) return '—';
  const formatted = Math.abs(num).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return num >= 0 ? `+${formatted}` : `-${formatted}`;
};

// --> Colour class for any P&L value
const pnlColor = (val) => {
  if (val == null || isNaN(val)) return 'text-fg-2';
  return val >= 0 ? 'text-profit' : 'text-loss';
};

// --> IST time formatter for broker snapshot as_of timestamps
const _asOfFmt = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const formatAsOf = (asOf) => {
  if (!asOf) return null;
  try {
    return _asOfFmt.format(new Date(asOf));
  } catch {
    return null;
  }
};

/*>>> COLUMN DEFINITIONS <<<*/

// Full columns (order matters); 'strategy' is conditionally included
// R105.1: unrealized_pnl and realized_pnl are SEPARATE columns — a single combined column is FORBIDDEN
const FULL_COLUMNS = [
  { key: 'strategy',        label: 'Strategy' },
  { key: 'source',          label: 'Source' },        // R7.2 — Algo/Manual (OMS) vs Broker (BD)
  { key: 'instrument',      label: 'Instrument' },
  { key: 'side',            label: 'Side' },
  //! #109 Live-Val 2026-09-08 (Deepak) — Intraday vs Delivery/Carryforward must be visible on an
  //!  open position; both OMS canonical and broker-form product codes resolve through
  //!  resolveProductLabel (one label table, R2.1).
  { key: 'product',         label: 'Product' },
  { key: 'qty',             label: 'Qty',             align: 'right' },
  { key: 'avg_entry_price', label: 'Avg',             align: 'right' },
  { key: 'ltp',             label: 'LTP',             align: 'right' },
  { key: 'unrealized_pnl',  label: 'Unrealized',      align: 'right' },  // R105.1 separate column
  //! Realized on an OPEN row = P&L already booked on this position: partial exits (lots
  //!  closed so far) and charges on the fills so far. It is NOT the open lot's P&L.
  { key: 'realized_pnl',    label: 'Realized (gross)', align: 'right',
    title: 'Booked so far on this position: partial exits + charges on fills. Not the open lot\'s P&L.' },  // R105.1 separate column
  { key: 'reconcile_state', label: 'Reconcile' },     // R104.5
  { key: 'recovery',        label: 'Recovery' },      // R3.1 — self-service flatten control
];

// Compact columns — drop strategy, avg entry, realized P&L
const COMPACT_COLUMNS = [
  { key: 'source',          label: 'Source' },        // R7.2
  { key: 'instrument',      label: 'Instrument' },
  { key: 'side',            label: 'Side' },
  { key: 'product',         label: 'Product' },
  { key: 'qty',             label: 'Qty',             align: 'right' },
  { key: 'ltp',             label: 'LTP',             align: 'right' },
  { key: 'unrealized_pnl',  label: 'Unrealized',      align: 'right' },  // R105.1 separate column
  { key: 'reconcile_state', label: 'Reconcile' },     // R104.5
  { key: 'recovery',        label: 'Recovery' },      // R3.1 — self-service flatten control
];

//--> C-9: module-level WARN-once ledger. A NULL lifecycle_id on an OPEN (non-FLAT) position is
//    a data-completeness gap on the order-view mapper (WP7), not a routine per-render event —
//    warn exactly once per sii, never once per re-render/tick.
const _warnedNullLifecycleSii = new Set();

/*>>> SUB-COMPONENT: FlattenControl <<<*/

/**
 * FlattenControl — R3.1 self-service recovery flatten control for ONE position row.
 *
 * L2-WP5/F-CONS-5: the R3.4 recovery-status fetch is HOISTED to the parent `PositionsTable` —
 * ONE batch request per render covers every non-FLAT row (`getRecoveryStatusBatch`, api.js).
 * This component is now purely presentational for that part: it receives the resolved
 * `recoverable` value as a prop instead of fetching it itself.
 *
 * ENABLED iff `recoverable === true` (the batch GET's per-sii admission answer). Disabled +
 * explanatory tooltip otherwise:
 *  - `recoverable == null` (Redis unavailable server-side, request still in flight, the batch
 *    request failed, or this sii was omitted from the response) -> "Recovery status unavailable"
 *    — NEVER falls through to enabled (feedback_no_silent_safety_removal).
 *  - `recoverable === false` (position is healthy / protected)   -> "Protected — no recovery
 *    needed. Exits are placed by the strategy."
 *  - `lifecycle_id` is NULL on this OPEN row (C-9)                -> disabled + tooltip, WARN
 *    once, and this sii is excluded from the parent's batch request entirely (force-close
 *    requires lifecycle_id; nothing to check).
 *
 * Submission REUSES `services.selfServiceRecovery.forceClose` (api.js:1273) and
 * `RecoveryActionModal` UNMODIFIED — the `acknowledge_loss` gate already lives there.
 *
 * @param {Object} position - Position row (must carry strategy_instance_id, lifecycle_id).
 * @param {boolean|null} recoverable - Resolved by the parent's batch fetch; null/undefined
 *   while unresolved or on failure — always renders disabled (never a silent enable).
 */
function FlattenControl({ position, recoverable }) {
  const sii = position.strategy_instance_id;
  const lifecycleId = position.lifecycle_id ?? null;
  //* useSnackbar — success/failure toast on submit, same pattern as StuckOrdersPanel.
  const { showSnackbar } = useSnackbar() || {};

  /*>>> State <<<*/
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState(null);

  /*>>> Effects <<<*/
  //! C-9 — NULL lifecycle_id: nothing to recover (forceClose requires lifecycle_id). WARN once
  //  per sii instead of flooding the console on every re-render. Unchanged by the L2-WP5 hoist —
  //  this warning is independent of the (now-parent-owned) fetch.
  useEffect(() => {
    if (lifecycleId == null && !_warnedNullLifecycleSii.has(sii)) {
      _warnedNullLifecycleSii.add(sii);
      console.warn(
        '[DIAG] PositionsTable: OPEN position has NULL lifecycle_id — flatten control disabled',
        { sii }
      );
    }
  }, [sii, lifecycleId]);

  //--> L2-WP5: the R3.4 fetch itself is now the parent PositionsTable's single batch effect
  //    (getRecoveryStatusBatch) — `recoverable` arrives as a prop. Nothing to fetch here.

  /*>>> Handlers <<<*/
  const openModal = () => {
    setActionError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setActionError(null);
  };

  const confirmFlatten = async (form) => {
    setSubmitting(true);
    setActionError(null);
    try {
      //! REUSE services.selfServiceRecovery.forceClose (api.js:1273) — unmodified. The backend
      //  re-checks admission at submit time (409 if it has since changed); this control's
      //  ENABLED state is advisory, never the sole gate.
      await services.selfServiceRecovery.forceClose(sii, {
        lifecycle_id: lifecycleId,
        reason: form.reason || 'user_self_service_force_close',
        compliance_note: form.compliance_note,
        acknowledge_loss: true,
      });
      showSnackbar?.('Flatten request submitted.', 'success');
      closeModal();
    } catch (err) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.detail ||
        'Could not flatten this position. Please try again.';
      setActionError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  /*>>> Render <<<*/
  if (lifecycleId == null) {
    return (
      <Button
        variant="secondary"
        size="sm"
        disabled
        title="Recovery status unavailable — this position has no recorded lifecycle to close."
      >
        Flatten
      </Button>
    );
  }

  //! recoverable == null covers "in flight" and "resolved null" alike (Redis unavailable,
  //  the batch request failed, or this sii was omitted from the response) — never a silent
  //  fall-through to enabled (feedback_no_silent_safety_removal).
  if (recoverable == null) {
    return (
      <Button variant="secondary" size="sm" disabled title="Recovery status unavailable">
        Flatten
      </Button>
    );
  }

  if (recoverable === false) {
    return (
      <Button
        variant="secondary"
        size="sm"
        disabled
        title="Protected — no recovery needed. Exits are placed by the strategy."
      >
        Flatten
      </Button>
    );
  }

  const instrumentLabel =
    position.display_symbol ?? position.symbol ?? position.instrument_symbol ?? position.name ?? sii;

  return (
    <>
      <Button variant="danger" size="sm" onClick={openModal} aria-label={`Flatten position ${sii}`}>
        Flatten
      </Button>
      <RecoveryActionModal
        open={modalOpen}
        onClose={closeModal}
        onConfirm={confirmFlatten}
        mode="force-close"
        instrumentLabel={instrumentLabel}
        submitting={submitting}
        errorMessage={actionError}
      />
    </>
  );
}

/*>>> SUB-COMPONENT: PositionRow <<<*/

/**
 * PositionRow — isolated row that calls useLivePnL internally.
 * Only THIS row re-renders on a market tick; the parent table is unaffected.
 *
 * R105.2: broker_pnl cells (unrealized_pnl / realized_pnl with broker_as_of) are
 * rendered from the snapshot prop and MUST NOT be updated by live LTP ticks.
 *
 * @param {Object}  position     - Position object from useTradingData()
 * @param {Object[]} columns     - Resolved column definitions to render
 * @param {boolean|null} recoverable - L2-WP5: this row's resolved recovery-status admission
 *   answer from the parent table's single batch fetch (undefined for FLAT/no-lifecycle rows
 *   that were never in the batch request).
 */
function PositionRow({ position, columns, recoverable }) {
  //! Each PositionRow registers its own tick listener — isolated re-render per row
  const { unrealizedPnl, ltp } = useLivePnL(position.strategy_instance_id);

  //--> Prefer live tick values; fall back to snapshot values from OMS
  const displayLtp = ltp ?? position.ltp ?? null;

  //! R105.2: broker P&L cells are FROZEN — always read from the snapshot prop, never from live hook.
  //  If broker_as_of is present, this position's P&L came from a broker snapshot and must not drift.
  const hasBrokerSnapshot = !!position.broker_as_of;

  //! Live unrealized P&L: only use the live hook when there is NO broker snapshot.
  //  If the broker provided a snapshot, display that value frozen (R105.2).
  const displayUnrealizedPnl = hasBrokerSnapshot
    ? position.unrealized_pnl   // frozen broker snapshot
    : (ltp != null ? unrealizedPnl : position.unrealized_pnl);

  //--> Realized P&L always from snapshot (never live-computed)
  const displayRealized = position.realized_pnl;

  //--> D2: Extract readable instrument label — visible text NEVER shows the UUID.
  //   instrument-id-display-remediation S3 — prefer the stored canonical display_symbol
  //   (positions.display_symbol, stamped by the OrderProjector + backfilled), then the resolved
  //   symbol, then instrument_symbol/name; never a raw uuid, never a broker-wire leak.
  //   Priority: display_symbol → symbol → instrument_symbol → name → '—'
  const instrumentLabel =
    position.display_symbol ??
    position.symbol ??
    position.instrument_symbol ??
    position.name ??
    '—';

  //--> Extract a short strategy name from strategy_instance_id (last segment after last ':')
  const strategyLabel =
    position.strategy_name ??
    position.strategy ??
    (position.strategy_instance_id
      ? position.strategy_instance_id.split(':').slice(1, 3).join(' · ')
      : '—');

  //--> Side display config
  const isLong = position.side === 'LONG' || position.side === 'BUY';
  const sideLabel = isLong ? '▲ LONG' : '▼ SHORT';
  const sideColor = isLong ? 'text-profit' : 'text-loss';

  // --> Broker snapshot as_of display string (IST)
  const brokerAsOfLabel = hasBrokerSnapshot ? formatAsOf(position.broker_as_of) : null;

  /*>>> CELL RENDERER <<<*/
  const renderCell = (col) => {
    switch (col.key) {
      //! R7.2 — Source badge: ALGO/MANUAL (OMS belief) vs Broker (BD truth), keyed on `leg`.
      case 'source': {
        const cfg = getSourceTagBadge(position.leg);
        //! #109 Live-Val 2026-09-08 — show WHICH broker next to the source badge so "All
        //!  accounts / All brokers" still tells the user where a row lives (ICICI vs Flattrade).
        const brokerLabel = position.broker_display_name || position.broker || null;
        return (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <Badge variant={cfg.variant} size="sm">{cfg.label}</Badge>
            {brokerLabel && (
              <span className="text-xs text-fg-2" title={position.broker_client_id || undefined}>{brokerLabel}</span>
            )}
          </span>
        );
      }

      case 'strategy':
        return (
          <span className="max-w-[140px] truncate text-sm font-medium text-fg-1" title={strategyLabel}>
            {strategyLabel}
          </span>
        );

      case 'instrument': {
        // D2: tooltip shows human-readable symbol + exchange, never the UUID
        const instrTooltip = [instrumentLabel, position.exchange].filter(Boolean).join(' · ');
        return (
          <span className="whitespace-nowrap text-sm font-semibold text-fg-1" title={instrTooltip || instrumentLabel}>
            {instrumentLabel}
          </span>
        );
      }

      case 'side':
        return (
          <span className={`whitespace-nowrap text-sm font-semibold ${sideColor}`}>
            {sideLabel}
          </span>
        );

      case 'qty':
        return (
          <span className="font-mono text-sm tabular-nums text-fg-1">
            {position.qty ?? position.quantity ?? '—'}
          </span>
        );

      case 'avg_entry_price':
        return (
          <span className="font-mono text-sm tabular-nums text-fg-1">
            {formatPrice(position.avg_entry_price ?? position.avg_cost ?? position.entry_price)}
          </span>
        );

      case 'ltp':
        return (
          <span className="font-mono text-sm tabular-nums text-fg-1">
            {displayLtp != null ? formatPrice(displayLtp) : '—'}
          </span>
        );

      // R105.1 — Unrealized P&L: separate column, labeled "Unrealized"
      // R105.2 — If broker snapshot present, frozen from snapshot; sub-label shows "As of HH:MM:SS (broker snapshot)"
      case 'unrealized_pnl': {
        const pnlVal = displayUnrealizedPnl;
        const pnlNum = pnlVal != null ? (typeof pnlVal === 'number' ? pnlVal : parseFloat(pnlVal)) : null;
        return (
          <div className="flex flex-col items-end gap-0.5">
            <span
              className={`font-mono text-sm tabular-nums font-semibold ${pnlColor(pnlNum)}`}
              //! R105.2 — if broker snapshot, mark data-source for test isolation
              data-source={hasBrokerSnapshot ? 'broker-snapshot' : 'live'}
              title={hasBrokerSnapshot && brokerAsOfLabel
                ? `As of ${brokerAsOfLabel} (broker snapshot)`
                : undefined}
            >
              {formatPnl(pnlNum)}
            </span>
            {/* R105.2 — broker snapshot sub-label */}
            {hasBrokerSnapshot && brokerAsOfLabel && (
              <span className="whitespace-nowrap text-[10px] text-fg-2 tabular-nums" title={`Broker snapshot as of ${brokerAsOfLabel}`}>
                snapshot {brokerAsOfLabel}
              </span>
            )}
            {/* R105.2 — if broker P&L absent, label as DeeTrade indicative */}
            {!hasBrokerSnapshot && pnlVal != null && (
              <span className="whitespace-nowrap text-[10px] text-fg-2">indicative</span>
            )}
          </div>
        );
      }

      // R105.1 — Realized P&L: separate column, labeled "Realized"
      // R105.2 — same broker snapshot freeze logic
      case 'product': {
        const label = resolveProductLabel(position.product_type);
        const raw = position.product_type ? String(position.product_type) : '';
        const isIntraday = label === 'Intraday';
        return label ? (
          <Badge variant={isIntraday ? 'warning' : 'neutral'} size="sm" title={raw !== label ? raw : undefined}>{label}</Badge>
        ) : (
          <span className="text-xs text-fg-2" title="Broker did not report a product type">—</span>
        );
      }

      case 'realized_pnl': {
        const realVal = displayRealized;
        const realNum = realVal != null ? (typeof realVal === 'number' ? realVal : parseFloat(realVal)) : null;
        return (
          <div className="flex flex-col items-end gap-0.5">
            <span
              className={`font-mono text-sm tabular-nums ${pnlColor(realNum)}`}
              data-source={hasBrokerSnapshot ? 'broker-snapshot' : 'deetrade'}
              title={hasBrokerSnapshot && brokerAsOfLabel
                ? `As of ${brokerAsOfLabel} (broker snapshot)`
                : undefined}
            >
              {formatPnl(realNum)}
            </span>
            {hasBrokerSnapshot && brokerAsOfLabel && (
              <span className="whitespace-nowrap text-[10px] text-fg-2 tabular-nums" title={`Broker snapshot as of ${brokerAsOfLabel}`}>
                snapshot {brokerAsOfLabel}
              </span>
            )}
            {/* R105.1 — DeeTrade-computed without broker: label as gross/indicative, never silently blended */}
            {!hasBrokerSnapshot && realNum != null && realNum !== 0 && (
              <span className="whitespace-nowrap text-[10px] text-fg-2" title="Partial exits + charges booked so far; not the open lot's P&L">booked so far</span>
            )}
          </div>
        );
      }

      // R104.5 — Reconcile state
      // R104.11 — gate: never render a badge when reconcile_state is null
      //  (unclassified positions must show an empty cell, not a badge or a fallback)
      case 'reconcile_state':
        if (position.reconcile_state == null) return null;
        return <ReconcileBadge state={position.reconcile_state} />;

      // R3.1 — flatten control renders on every non-FLAT row only. L2-WP5: `recoverable` is
      // resolved by the parent table's ONE batch fetch, not fetched here.
      case 'recovery':
        if (position.side === 'FLAT') return null;
        return <FlattenControl position={position} recoverable={recoverable ?? null} />;

      default:
        return null;
    }
  };

  return (
    <DataTable.Row>
      {columns.map((col) => (
        <DataTable.Cell
          key={col.key}
          align={col.align}
        >
          {renderCell(col)}
        </DataTable.Cell>
      ))}
    </DataTable.Row>
  );
}

/*>>> MAIN COMPONENT <<<*/

/**
 * PositionsTable — reusable positions table with per-row live P&L.
 *
 * Each row is a PositionRow sub-component that calls useLivePnL() independently,
 * so only the affected row re-renders when a market tick arrives.
 *
 * R105.1: unrealized_pnl and realized_pnl are SEPARATE columns (combined column is FORBIDDEN).
 * R105.2: broker snapshot P&L cells are frozen — not updated by live ticks.
 * R104.5: reconcile_state column added.
 *
 * @param {Object[]} positions    - Array of position objects from useTradingData()
 * @param {boolean}  showStrategy - Show strategy name column (default: true)
 * @param {boolean}  compact      - Fewer columns for embedded views (default: false)
 * @param {string}   maxHeight    - Scrollable height CSS value (default: none)
 */
const PositionsTable = ({
  positions = [],
  showStrategy = true,
  compact = false,
  maxHeight,
}) => {
  /*>>> DERIVED STATE <<<*/

  //--> Pick column set, then strip strategy column when not needed
  const baseColumns = compact ? COMPACT_COLUMNS : FULL_COLUMNS;
  const columns = baseColumns.filter((col) => {
    if (col.key === 'strategy' && !showStrategy) return false;
    return true;
  });

  /*>>> R3.1/L2-WP5/F-L3-2 — ONE recovery-status batch request per render, at FULL row grain <<<*/
  //! F-CONS-5: this used to be a per-row `useEffect` inside `FlattenControl` (one GET per
  //  non-FLAT row, N+1 on the admission chokepoint). Hoisted here so the whole table issues
  //  exactly ONE request covering every qualifying row; results are handed down as a
  //  `recoverable` prop. Must run before any early return (Rules of Hooks).
  //! F-L3-2: keyed by sii ALONE, this collided rows that share a strategy_instance_id but
  //  differ by instrument_id/account_id — the admission decision is made at ROW grain, not
  //  sii grain. Keyed here by the SAME composite the batch endpoint keys its response by
  //  (C-1, `eng-tech-lead-decompose.md`): `${sii}|${instrument_id}|${account_id ?? ''}`.
  //! F-3-12: the effect below chunks the deduped sii list into <=200-sii slices (server cap,
  //  self_service_recovery.py:576-588) — still ONE effect, ONE api method, no new component.
  const [recoverableByRow, setRecoverableByRow] = useState(() => new Map());

  //--> Deduped rows needing an answer: non-FLAT, lifecycle_id present, AND instrument_id
  //    present. C-9: a NULL lifecycle_id row is excluded here — never fetched, matching
  //    FlattenControl's own disabled-without-a-request branch. F-L3-2: a row lacking
  //    instrument_id is unresolvable at the required grain — excluded (disabled via the
  //    missing-key fail-closed branch below), NEVER falls back to a sii-only lookup.
  const recoveryRowsByKey = new Map();
  positions
    .filter((p) => p.side !== 'FLAT' && p.lifecycle_id != null && p.instrument_id != null)
    .forEach((p) => {
      const key = `${p.strategy_instance_id}|${p.instrument_id}|${p.account_id ?? ''}`;
      if (!recoveryRowsByKey.has(key)) {
        recoveryRowsByKey.set(key, {
          key,
          sii: p.strategy_instance_id,
          instrumentId: p.instrument_id,
          accountId: p.account_id ?? null,
        });
      }
    });
  const recoveryRows = Array.from(recoveryRowsByKey.values()).sort((a, b) =>
    a.key.localeCompare(b.key)
  );
  const recoveryRowsKey = recoveryRows.map((r) => r.key).join(',');

  useEffect(() => {
    if (recoveryRows.length === 0) {
      setRecoverableByRow(new Map());
      return undefined;
    }
    let cancelled = false;

    //--> F-3-12: chunk the DEDUPED sii list to <=200 per request — the server cap is a 422
    //    above 200 sii (self_service_recovery.py:576-588). Each chunk's ROWS (not just its
    //    siis) are tracked so a failed chunk nulls ONLY that chunk's rows, never every row
    //    (the prior single-request catch branch silently disabled the whole table).
    const RECOVERY_STATUS_SII_CHUNK_SIZE = 200;
    const dedupedSiis = [...new Set(recoveryRows.map((r) => r.sii))];
    const rowChunks = [];
    for (let i = 0; i < dedupedSiis.length; i += RECOVERY_STATUS_SII_CHUNK_SIZE) {
      const chunkSiis = new Set(dedupedSiis.slice(i, i + RECOVERY_STATUS_SII_CHUNK_SIZE));
      rowChunks.push(recoveryRows.filter((row) => chunkSiis.has(row.sii)));
    }

    Promise.allSettled(
      rowChunks.map((chunkRows) => services.selfServiceRecovery.getRecoveryStatusBatch(chunkRows))
    ).then((results) => {
      if (cancelled) return;
      //--> Merge every chunk's row map into ONE result map; a rejected chunk contributes
      //    null (disabled) for ONLY its own rows, a fulfilled chunk contributes its answers.
      const next = new Map();
      results.forEach((result, idx) => {
        const chunkRows = rowChunks[idx];
        if (result.status === 'fulfilled') {
          const data = result.value?.data?.data || result.value?.data || {};
          const statuses = data.statuses || {};
          chunkRows.forEach((row) => {
            //! recoverable: null (service_not_ready / Redis unavailable / ambiguous_scope) or
            //  a row key omitted from the response is a DISABLED state, never a silent
            //  fall-through to enabled (feedback_no_silent_safety_removal).
            next.set(row.key, statuses[row.key]?.recoverable ?? null);
          });
        } else {
          console.log('[DIAG] PositionsTable: recovery-status batch fetch failed', {
            rowKeys: chunkRows.map((r) => r.key),
            message: result.reason?.message,
          });
          //! Chunk failure -> ONLY this chunk's rows disabled/null, never the whole table.
          chunkRows.forEach((row) => next.set(row.key, null));
        }
      });
      setRecoverableByRow(next);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoveryRowsKey]);

  /*>>> EMPTY STATE <<<*/
  if (positions.length === 0) {
    return (
      <div className="overflow-hidden rounded-xl border border-[var(--divider)] bg-bg-2">
        <div className="flex items-center justify-center py-12 text-center">
          <span className="text-sm text-fg-2">No open positions</span>
        </div>
      </div>
    );
  }

  /*>>> RENDER <<<*/
  return (
    <div className="overflow-hidden rounded-xl border border-[var(--divider)] bg-bg-2">
      <div
        className="overflow-x-auto"
        style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}
      >
        <DataTable className="w-full">
          {/* --> Table header */}
          <DataTable.Head>
            <DataTable.Row>
              {columns.map((col) => (
                <DataTable.HeaderCell
                  key={col.key}
                  align={col.align || 'left'}
                  title={col.title || undefined}
                >
                  {col.label}
                </DataTable.HeaderCell>
              ))}
            </DataTable.Row>
          </DataTable.Head>

          {/* --> Table body — each row manages its own live P&L subscription */}
          <DataTable.Body>
            {positions.map((position, idx) => {
              const key = derivePositionRowKey(position) ?? `position-${idx}`;
              //! F-L3-2: read the SAME full-grain composite key the fetch effect above builds
              //  and dedupes by — a missing key (row excluded from the batch, e.g. no
              //  instrument_id) resolves to `undefined`, which downstream is treated exactly
              //  like `null`: disabled, never enabled.
              const recoveryKey = `${position.strategy_instance_id}|${position.instrument_id}|${position.account_id ?? ''}`;
              return (
                <PositionRow
                  key={key}
                  position={position}
                  columns={columns}
                  recoverable={recoverableByRow.get(recoveryKey)}
                />
              );
            })}
          </DataTable.Body>
        </DataTable>
      </div>

      {/* --> Footer */}
      <div className="flex items-center border-t border-[var(--divider)] bg-bg-2 px-4 py-2">
        <span className="text-xs text-fg-2">
          {positions.length} open {positions.length === 1 ? 'position' : 'positions'}
        </span>
      </div>
    </div>
  );
};

export default PositionsTable;
